import React from 'react';
import { Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Stack } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { listUsers, deleteUser } from '../api/users';
import type { UserDto, ListUsersResponse } from '../api/users';
import { useNavigate } from 'react-router-dom';
import { Pagination, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

const UsersListPage: React.FC = () => {
  const [users, setUsers] = React.useState<UserDto[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [page, setPage] = React.useState<number>(1);
  const [limit] = React.useState<number>(10);
  const [total, setTotal] = React.useState<number>(0);
  const [q, setQ] = React.useState<string>('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res: ListUsersResponse = await listUsers({ page, limit, q: q || undefined });
      setUsers(res.data);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [page, limit, q]);

  React.useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    if (!confirm(t('users.confirmDelete', '¿Eliminar usuario?'))) return;
    await deleteUser(id);
    await load();
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t('users.title', 'Usuarios')}</Typography>
        <Stack direction="row" spacing={2}>
          <TextField size="small" placeholder={t('common.search', 'Buscar...')} value={q} onChange={e => { setPage(1); setQ(e.target.value); }} InputProps={{ startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ) }} />
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/users/new')}>{t('users.new', 'Nuevo Usuario')}</Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>{t('common.user', 'Usuario')}</TableCell>
              <TableCell>{t('common.status', 'Estado')}</TableCell>
              <TableCell>{t('common.created', 'Creado')}</TableCell>
              <TableCell>{t('common.updated', 'Actualizado')}</TableCell>
              <TableCell align="right">{t('common.actions', 'Acciones')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && users.map(u => (
              <TableRow key={u.id} hover>
                <TableCell>{u.id}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.username}</TableCell>
                <TableCell>
                  <Chip size="small" color={u.isActive ? 'success' : 'default'} label={u.isActive ? t('common.active', 'Activo') : t('common.inactive', 'Inactivo')} />
                </TableCell>
                <TableCell>{new Date(u.createdAt).toLocaleString()}</TableCell>
                <TableCell>{new Date(u.updatedAt).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<Edit />} onClick={() => navigate(`/users/${u.id}/edit`)}>{t('common.edit', 'Editar')}</Button>
                  <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(u.id)}>{t('common.delete', 'Eliminar')}</Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">{t('users.empty', 'Sin usuarios')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
        <Pagination page={page} onChange={(_, p) => setPage(p)} count={Math.max(1, Math.ceil(total / limit))} color="primary" />
      </Stack>
    </Box>
  );
};

export default UsersListPage;


